import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMAIL_APP_STORE } from '../../email-app.store';
import { EmailOAuthService, EmailProvider } from '../../services/email-oauth.service';

@Component({
  selector: 'oiq-email-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="callback-container">
      <div class="callback-card">
        @if (isLoading) {
          <div class="loading">
            <div class="spinner"></div>
            <h2>Processing OAuth callback...</h2>
            <p>Please wait while we complete the authentication.</p>
          </div>
        } @else if (error) {
          <div class="error-state">
            <h2>Authentication Failed</h2>
            <p>{{ error }}</p>
            <button (click)="goHome()" class="retry-button">Try Again</button>
          </div>
        } @else if (success) {
          <div class="success-state">
            <div class="success-icon">✓</div>
            <h2>Authentication Successful!</h2>
            <p>Your {{ getProviderDisplayName() }} account has been connected successfully.</p>
            <button (click)="goHome()" class="continue-button">Continue</button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .callback-container {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }

      .callback-card {
        background: white;
        border-radius: 12px;
        padding: 40px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        text-align: center;
        max-width: 400px;
        width: 100%;
      }

      .loading,
      .error-state,
      .success-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
      }

      .spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #222;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .success-icon {
        width: 60px;
        height: 60px;
        background: #4caf50;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 30px;
        font-weight: bold;
      }

      h2 {
        margin: 0;
        color: #333;
        font-size: 24px;
      }

      p {
        margin: 0;
        color: #666;
        line-height: 1.5;
      }

      .retry-button,
      .continue-button {
        padding: 12px 24px;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .retry-button {
        background: #f44336;
        color: white;
      }

      .retry-button:hover {
        background: #d32f2f;
      }

      .continue-button {
        background: #4caf50;
        color: white;
      }

      .continue-button:hover {
        background: #45a049;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class EmailCallbackPage implements OnInit {
  private emailStore = inject(EMAIL_APP_STORE);

  isLoading = true;
  error: string | null = null;
  success = false;
  provider: EmailProvider | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private oauthService: EmailOAuthService
  ) {}

  ngOnInit() {
    this.handleCallback();
  }

  private handleCallback() {
    // Extract provider from URL path
    const urlPath = this.router.url;
    let provider: EmailProvider | null = null;

    if (urlPath.includes('/oauth/gmail/callback')) {
      provider = 'gmail';
    } else if (urlPath.includes('/oauth/outlook/callback')) {
      provider = 'outlook';
    } else {
      // Fallback: check query parameters or use current provider from service
      this.route.queryParams.subscribe(params => {
        provider =
          (params['provider'] as EmailProvider) || this.oauthService.currentProvider() || null;
      });
    }

    if (!provider) {
      this.isLoading = false;
      this.error = 'Unable to determine email provider';
      return;
    }

    this.provider = provider;

    // Get query parameters from the URL
    this.route.queryParams.subscribe(params => {
      const code = params['code'];
      const error = params['error'];
      const state = params['state'];

      if (error) {
        this.isLoading = false;
        this.error = `OAuth error: ${error}`;
        return;
      }

      if (code) {
        // Successfully received authorization code
        this.processAuthorizationCode(code, state, provider);
      } else {
        this.isLoading = false;
        this.error = 'No authorization code received';
      }
    });
  }

  private async processAuthorizationCode(code: string, state?: string, provider?: EmailProvider) {
    if (!provider) {
      this.isLoading = false;
      this.error = 'Provider is required for processing authorization code';
      return;
    }
    try {
      // Call the Go backend with the OAuth params

      const response = await this.oauthService.handleOAuthCallback(code, state || '', provider);

      this.isLoading = false;
      this.success = true;

      // Store success state in localStorage for the main app (provider-specific)
      localStorage.setItem(`oauth_success_${provider}`, 'true');
      localStorage.setItem(`oauth_timestamp_${provider}`, Date.now().toString());
      localStorage.setItem(`oauth_response_${provider}`, JSON.stringify(response));

      this.emailStore.reloadConnectedProviders();

      // Auto-redirect to home after 1.5 seconds (reduced time)
      setTimeout(() => {
        this.goHome();
      }, 1500);
    } catch (error) {
      this.isLoading = false;
      this.error = 'Failed to process authorization code';
      console.error('Callback processing error:', error);
    }
  }

  getProviderDisplayName(): string {
    if (!this.provider) return 'Email Provider';
    return this.provider === 'gmail' ? 'Gmail' : 'Outlook';
  }

  goHome() {
    this.router.navigate(['/']);
  }
}
